<template>
  <div class="w-full h-full space-y-4">
    <AlertDialog :open="showManualCashbackUpdate" @update:open="(o) => { if (!o) { showManualCashbackUpdate = false;} }">
      <AlertDialogContent class="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Update cashback object</AlertDialogTitle>
          <AlertDialogDescription>Change the status of a cashback object</AlertDialogDescription>
        </AlertDialogHeader>
        <div class="grid gap-3">
          <Select v-model="updateCashbackRequest.status">
            <SelectTrigger class="w-full">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="o in cashbackStatuses" :key="String(o.id)" :value="o.id">{{ o.text }}</SelectItem>
            </SelectContent>
          </Select>
          <Select v-if="updateCashbackRequest.status == 'VERIFIED'" v-model="spendAmountChangeStr">
            <SelectTrigger class="w-full">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="o in trueOrFalse" :key="String(o.id)" :value="String(o.id)">{{ o.text }}</SelectItem>
            </SelectContent>
          </Select>
          <Input v-if="updateCashbackRequest.status == 'VERIFIED' && updateCashbackRequest.spendAmountChange" v-model="updateCashbackRequest.spendAmount" placeholder="Spend amount" />
        </div>
        <AlertDialogFooter class="gap-2">
          <AlertDialogCancel @click="showManualCashbackUpdate = false; selectedCashback = undefined">Cancel</AlertDialogCancel>
          <AlertDialogAction :disabled="updateCashbackRequest.status == undefined" @click="updateCashbackById()">Update cashback</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <Card>
      <CardHeader>
        <CardTitle>Cashback ledger</CardTitle>
        <CardDescription>View the status of cashback objects</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable :headers="headersTable1" :items="cashbackObjects" :loading="loading"
          v-model:server-options="serverOptions" :server-items-length="serverItemsLength"
          searchable
          search-placeholder="Search by cashback id, transaction id, or user id"
          :search="search"
          @update:search="search = $event"
          @search="runSearch()"
          @clear-search="onClearSearch()"
          :filter-groups="statusFilterGroups"
          :filters="statusFilters"
          @filter-change="onStatusFilterChange"
          :error="error"
          @retry="getCashbackObjects(serverOptions.page - 1, serverOptions.rowsPerPage)">
          <template #item-id="id">
            <CopyId v-if="rowCashbackId(id)" :value="rowCashbackId(id)" :max-width="120" />
            <span v-else class="text-xs text-muted-foreground">—</span>
          </template>
          <template #item-createdOn="createdOn">
            <span class="text-xs whitespace-nowrap">{{ niceDateTime(createdOn.createdOn) }}</span>
          </template>
          <template #item-status="status">
            <div class="flex items-center gap-2">
              <span class="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
                <component
                  :is="statusIcon(status.status)"
                  :class="[
                    'size-3.5',
                    statusKind(status.status) === 'done' ? 'text-green-500 dark:text-green-400' : '',
                    statusKind(status.status) === 'failed' ? 'text-red-500 dark:text-red-400' : '',
                    statusKind(status.status) === 'progress' ? 'animate-spin' : '',
                  ]"
                />
                {{ formatStatus(status.status) }}
              </span>
              <button
                v-if="showTestCashbackButton && rowCashbackId(status)"
                type="button"
                class="text-xs font-medium text-primary underline-offset-2 hover:underline"
                @click="() => openStatusUpdate(rowCashbackId(status))"
              >
                Update status
              </button>
            </div>
          </template>
          <template #item-internalEndUserId="internalEndUserId">
            <router-link
              v-if="internalEndUserId.endUserId"
              :to="`/enrolled-users/users/${internalEndUserId.endUserId}`"
              class="text-xs font-mono font-medium whitespace-nowrap text-sky-600 dark:text-sky-400 hover:text-sky-500 dark:hover:text-sky-300 hover:underline underline-offset-2 transition-colors"
              @click.stop
            >
              {{ internalEndUserId.endUserId }}
            </router-link>
            <p v-else class="text-xs font-mono whitespace-nowrap">—</p>
          </template>
          <template #item-internalTransactionId="internalEndUserId">
            <CopyId v-if="internalEndUserId.transactionId" :value="internalEndUserId.transactionId" :max-width="160" />
            <p v-else class="text-xs text-muted-foreground">—</p>
          </template>
          <template #item-retailer="cashback">
            <div v-if="cashback.programOffer && cashback.programOffer.retailer" class="flex items-center gap-2">
              <img v-if="cashback.programOffer.retailer.logo" :src="cashback.programOffer.retailer.logo" class="w-6 h-6 rounded object-contain border bg-white" alt="" />
              <span class="text-sm font-medium truncate max-w-40" :title="cashback.programOffer.retailer.retailerName">{{ cashback.programOffer.retailer.retailerName }}</span>
            </div>
            <div v-else class="flex items-center gap-2">
              <img src="cardlyticsIcon.png" class="w-6 h-6 rounded object-contain border bg-white" alt="" />
              <span class="text-sm font-medium whitespace-nowrap">Cardlytics</span>
            </div>
          </template>
          <template #item-transactionValue="cashback">
            <span v-if="cashback.transactionAmount != null" class="text-sm tabular-nums whitespace-nowrap">{{ formatMoney(cashback.transactionAmount.amount, cashback.transactionAmount.currency) }}</span>
            <span v-else class="text-sm text-muted-foreground whitespace-nowrap">Affiliate Transaction</span>
          </template>
          <template #item-userReceivable="cashback">
            <span v-if="cashback.payoutData" class="text-sm tabular-nums whitespace-nowrap">{{ formatMoney(cashback.payoutData.endUserCashbackReceivable, cashback.payoutData.currency) }}</span>
            <span v-else class="text-sm text-muted-foreground">—</span>
          </template>
          <template #item-publisherReceivable="cashback">
            <span v-if="cashback.payoutData" class="text-sm tabular-nums whitespace-nowrap">{{ formatMoney(cashback.payoutData.publisherCashbackReceivable, cashback.payoutData.currency) }}</span>
            <span v-else class="text-sm text-muted-foreground">—</span>
          </template>
          <template #item-declinedReason="cashback">
            <Badge v-if="cashback.declinedReason" variant="destructive" class="whitespace-nowrap">{{ cashback.declinedReason }}</Badge>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import { DateTime } from 'luxon'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
  Input,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  CopyId,
  toast,
} from '@boogi/ui'
import { Filter, X, CircleCheck, CircleX, Loader } from 'lucide-vue-next'
import { DataTable } from '@boogi/ui'

export default {
  name: 'Cashback-ledger',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    Button,
    Badge,
    Input,
    Checkbox,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
    CopyId,
    Filter,
    X,
  },
  props: {},

  data() {
    return {
      lastRequest: 'ALL', // ALL / USER / ID
      showFilters: false,
      showTestCashbackButton: localStorage.getItem('apiUrl').includes('sandbox'),
      showManualCashbackUpdate: false,
      selectedCashback: undefined,
      updateCashbackRequest: {
        status: undefined,
        spendAmountChange: false,
        spendAmount: undefined,
      },

      trueOrFalse: [
        { id: true, text: 'Yes' },
        { id: false, text: 'No' },
      ],

      cashbackStatuses: [
        { id: 'BEING_VERIFIED', text: 'BEING_VERIFIED' },
        { id: 'CREATED', text: 'CREATED' },
        { id: 'DECLINED', text: 'DECLINED' },
        { id: 'DISQUALIFIED', text: 'DISQUALIFIED' },
        { id: 'PAID', text: 'PAID' },
        { id: 'RECEIVED', text: 'RECEIVED' },
        { id: 'REFUNDED', text: 'REFUNDED' },
        { id: 'VERIFIED', text: 'VERIFIED' },
      ],

      // Default table filter must cover every status the update modal can set,
      // otherwise a row moved to e.g. DISQUALIFIED disappears on refresh and the
      // change looks like it never persisted.
      selectStatus: [
        { id: 'BEING_VERIFIED', text: 'BEING_VERIFIED' },
        { id: 'CREATED', text: 'CREATED' },
        { id: 'DECLINED', text: 'DECLINED' },
        { id: 'DISQUALIFIED', text: 'DISQUALIFIED' },
        { id: 'PAID', text: 'PAID' },
        { id: 'RECEIVED', text: 'RECEIVED' },
        { id: 'REFUNDED', text: 'REFUNDED' },
        { id: 'VERIFIED', text: 'VERIFIED' },
      ],

      resetStatus: [
        { id: 'BEING_VERIFIED', text: 'BEING_VERIFIED' },
        { id: 'CREATED', text: 'CREATED' },
        { id: 'DECLINED', text: 'DECLINED' },
        { id: 'DISQUALIFIED', text: 'DISQUALIFIED' },
        { id: 'PAID', text: 'PAID' },
        { id: 'RECEIVED', text: 'RECEIVED' },
        { id: 'REFUNDED', text: 'REFUNDED' },
        { id: 'VERIFIED', text: 'VERIFIED' },
      ],

      statuses: [
        { id: 'BEING_VERIFIED', text: 'BEING_VERIFIED' },
        { id: 'CREATED', text: 'CREATED' },
        { id: 'DECLINED', text: 'DECLINED' },
        { id: 'DISQUALIFIED', text: 'DISQUALIFIED' },
        { id: 'PAID', text: 'PAID' },
        { id: 'RECEIVED', text: 'RECEIVED' },
        { id: 'REFUNDED', text: 'REFUNDED' },
        { id: 'VERIFIED', text: 'VERIFIED' },
      ],

      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,
      tableType: 'all',
      search: "",

      userSearch: {
        userId: '',
      },
      loading: true,
      error: false,
      headersTable1: [
        { text: 'Retailer', value: 'retailer' },
        { text: 'Cashback ID', value: 'id' },
        { text: 'Status', value: 'status' },
        { text: 'User', value: 'internalEndUserId' },
        { text: 'Transaction value', value: 'transactionValue' },
        { text: 'End user receivable', value: 'userReceivable' },
        { text: 'Publisher receivable', value: 'publisherReceivable' },
        { text: 'Transaction id', value: 'internalTransactionId' },
        { text: 'Date created', value: 'createdOn' },
        { text: 'Declined reason', value: 'declinedReason' },
      ],
      cashbackObjects: [],
    }
  },
  created() {
    const initialSearch = (this.$route.query.search || '').trim()
    if (initialSearch) {
      this.search = initialSearch
      this.runSearch()
    } else {
      this.getCashbackObjects(0, 50)
    }
  },
  methods: {
    onClearSearch() {
      this.search = ''
      this.selectStatus = [...this.resetStatus]
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      this.getCashbackObjects(0, this.serverOptions.rowsPerPage)
    },
    onStatusFilterChange(updated) {
      const ids = (updated && updated.status) || []
      this.selectStatus = this.statuses.filter((s) => ids.includes(s.id))
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      this.getCashbackObjects(0, this.serverOptions.rowsPerPage)
    },
    formatMoney(amount, currency) {
      if (amount == null) return ''
      const code = (currency || 'GBP').toUpperCase()
      try {
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: code,
        }).format(amount)
      } catch (e) {
        return `${code} ${(Math.round(amount * 100) / 100).toFixed(2)}`
      }
    },
    formatStatus(status) {
      if (!status) return ''
      return String(status)
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    },

    statusKind(status) {
      if (['PAID', 'RECEIVED', 'VERIFIED'].includes(status)) return 'done'
      if (['DECLINED', 'DISQUALIFIED', 'REFUNDED'].includes(status)) return 'failed'
      return 'progress'
    },
    statusIcon(status) {
      const kind = this.statusKind(status)
      if (kind === 'done') return CircleCheck
      if (kind === 'failed') return CircleX
      return Loader
    },
    async runSearch() {
      const term = (this.search || '').trim()
      if (!term) return
      this.serverOptions = { page: 1, rowsPerPage: 50 }
      this.loading = true
      this.error = false
      this.tableType = 'search'
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const headers = { 'Content-Type': 'application/json', Authorization: `${token}` }
        const urls = [
          `${apiUrl}/private/api/client/cashback-ledger/cashback/${term}`,
          `${apiUrl}/private/api/client/cashback-ledger/transaction/${term}?page=0&size=50`,
          `${apiUrl}/private/api/client/cashback-ledger/user/${term}?page=0&size=50`,
        ]
        const results = await Promise.allSettled(
          urls.map((u) => this.$apiFetch(u, { method: 'GET', headers }).then((r) => (r.ok ? r.json() : null)).catch(() => null))
        )
        const merged = new Map()
        for (const r of results) {
          if (r.status !== 'fulfilled' || !r.value) continue
          const items = Array.isArray(r.value?.content) ? r.value.content : r.value?.id ? [r.value] : []
          for (const item of items) if (item && item.id) merged.set(item.id, item)
        }
        this.cashbackObjects = Array.from(merged.values())
        this.serverItemsLength = this.cashbackObjects.length
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },
    async getCashbackObjectsForAnEndUser(page, size, search) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
      this.tableType = "endUser"

      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/cashback-ledger/user/${search}?page=${page}&size=${size}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.serverItemsLength = object.totalElements
          this.cashbackObjects = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async getCashbackObjectsForATransaction(page, size, search) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false
      this.tableType = "transaction"

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/cashback-ledger/transaction/${search}?page=${page}&size=${size}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.serverItemsLength = 1
          this.cashbackObjects = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async getCashbackFromId(search) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/cashback-ledger/cashback/${search}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          var array = []
          this.serverItemsLength = 1
          array.push(object)

          this.cashbackObjects = array
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    // Resolve a ledger row's cashback id. The ledger DTO exposes it as `id`, but the
    // sandbox/playground shapes have surfaced the same value under `cashbackId` or the
    // raw Mongo `_id`, so accept any of them rather than blindly reading `.id`.
    rowCashbackId(row) {
      return row.id || row.cashbackId || row._id
    },
    // Open the status modal with a clean request so a prior edit's spendAmountChange
    // / spendAmount / status can't leak into the next cashback's update.
    openStatusUpdate(cashbackId) {      
      // Guard against a row that arrived without an id — otherwise the update POST
      // would hit /cashback-ledger/cashback/undefined and 404.
      if (!cashbackId) {
        toast.error('This cashback has no id, so its status cannot be updated.')
        return
      }
      this.updateCashbackRequest = { status: undefined, spendAmountChange: false, spendAmount: undefined }
      this.selectedCashback = cashbackId
      this.showManualCashbackUpdate = true
    },
    async updateCashbackById() {
      console.log(this.selectedCashback)
      if (!this.selectedCashback) {
        toast.error('This cashback has no id, so its status cannot be updated.')
        return
      }
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/cashback-ledger/cashback/${this.selectedCashback}`,
          {
            method: 'POST',
            body: JSON.stringify(this.updateCashbackRequest),
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.showManualCashbackUpdate = false
          this.selectedCashback = undefined
          this.getCashbackObjects(this.serverOptions.page - 1, this.serverOptions.rowsPerPage)
        }
      } catch (error) {
        return
      }
    },

    async getCashbackObjects(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
      this.tableType = "all"

      if (this.selectStatus.length > 0) {
        var selectStatus = []
        this.selectStatus.forEach((element) => {
          selectStatus.push(element.id)
        })
      }

      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/cashback-ledger?page=${page}&size=${size}&status=${selectStatus}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.cashbackObjects = object.content
          this.serverItemsLength = object.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {
    spendAmountChangeStr: {
      get() {
        return this.updateCashbackRequest.spendAmountChange ? 'true' : 'false'
      },
      set(value) {
        this.updateCashbackRequest.spendAmountChange = value === 'true'
      },
    },
    statusFilterGroups() {
      return [
        {
          key: 'status',
          label: 'Cashback status',
          options: this.statuses.map((s) => ({ value: s.id, label: this.formatStatus(s.id) })),
        },
      ]
    },
    statusFilters() {
      return { status: this.selectStatus.map((s) => s.id) }
    },
  },
  mounted() {
    document.title = 'Dashboard | Cashback ledger'
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        switch (this.tableType) {
          case "all":
            this.getCashbackObjects(val.page - 1, val.rowsPerPage)
            break

          case "endUser":
            this.getCashbackObjectsForAnEndUser(
              val.page - 1,
              val.rowsPerPage,
              this.search
            )
            break

          case "transaction":
            this.getCashbackObjectsForATransaction(
              val.page - 1,
              val.rowsPerPage,
              this.search
            )
            break
        }
      },
    }
  },
}
</script>
